import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  IconButton,
  List,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { SkeletonList } from 'src/shared/skeleton/skeleton-list';
import { Link as RouterLink, generatePath } from 'react-router-dom';
import { APP_PATH } from 'src/config/app-paths';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { generateDataGridRoute } from 'src/feature/data-grid/use-data-grid-search-params/generate-data-grid-route';
import { InvoiceListItem } from 'src/shared/invoice-list-item/invoice.list-item';
import { useGetInvoicesPaginatedQuery } from '@service/invoice-v1.service';

type CustomerInvoiceListProps = CardProps & {
  pseudo?: string;
};

export function CustomerInvoiceList(
  props: Readonly<CustomerInvoiceListProps>,
): React.JSX.Element {
  const { pseudo = '', sx, ...cardProps } = props;
  const { t } = useTranslation();

  const { data, isFetching, isError, isUninitialized } =
    useGetInvoicesPaginatedQuery(
      {
        limit: 3,
        offset: 0,
        pseudo,
        sort: {
          createdAt: -1,
        },
      },
      {
        skip: !pseudo,
      },
    );

  return (
    <>
      <Card sx={{ overflow: 'unset', ...sx }} {...cardProps}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'primary.light' }}>
              <RequestQuoteIcon color="primary" />
            </Avatar>
          }
          title={t('customer.detail.invoice-v1-section')}
        />

        <CardContent>
          {isFetching || isUninitialized ? (
            <SkeletonList />
          ) : (
            <List>
              {data?.docs.length === 0
                ? t('customer.detail.no-invoice-v1')
                : data?.docs.map((invoice) => (
                    <InvoiceListItem
                      key={invoice.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          component={RouterLink}
                          target="_blank"
                          to={generatePath(APP_PATH.INVOICE_HTML_V1, {
                            invoiceId: invoice.id,
                          })}>
                          <OpenInNewIcon />
                        </IconButton>
                      }
                      createdAt={new Date(invoice.createdAt)}
                      totalTTC={invoice.details.session.totalCost}
                    />
                  ))}
            </List>
          )}
        </CardContent>

        {pseudo && data?.hasNextPage && (
          <CardActions>
            <Button
              component={RouterLink}
              target="_blank"
              to={generateDataGridRoute(APP_PATH.LIST_INVOICE_V1, {
                filter: { pseudo },
              })}>
              {t('customer.detail.see-more')}
            </Button>
          </CardActions>
        )}
      </Card>

      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-invoice-v1-query')}
        />
      )}
    </>
  );
}
